import jQuery from 'jquery';
import { ZIDX } from "./standaloneLibrary.js";
import { ZIDXBase } from "./base.js";
import { AIChatBox, AIChatBoxConfig } from "./ai/AIChatBox.js";
import { SearchParams } from "./SearchParams.js";
import { MarkdownStreamParser } from "../ai/MarkdownStreamParser.js";

const $ = jQuery;

/**
 * Integrates AI-powered natural-language search into the existing
 * search results page.
 */
export class AISearchIntegration {
    private zidx: ZIDX;
    private base: ZIDXBase;
    private root: any;

    private active = false;
    private aiChatBox: AIChatBox | null = null;
    private $overlay: JQuery | null = null;
    private aiSessionId: string | null = null;

    /** Tracks whether the current prompt produced a successful search result */
    private lastPromptHadResults = false;

    /** True while we are restoring from a session URL / history load */
    private restoringSession = false;

    /** Tracks if we are currently holding a saved search state without an active chat session */
    private isSavedSearchMode = false;
    private savedSearchParams: SearchParams | null = null;

    /** The original URL path before AI search was activated */
    private originalPath: string | null = null;

    /** Secondary markdown parser that renders into the visible result bar */
    private barParser: MarkdownStreamParser | null = null;

    /** Stored summary text from last successful search (for collapse/expand toggle) */
    private lastSummaryText: string = "";

    querySummary: string = "";
    aiAdvancedSearchQueries: Map<number, string> = new Map();
    aiVectorSearchQueries: Map<number, string> = new Map();
    aiFieldWeights: Record<string, any> = {};
    aiSearchText: string = "";

    constructor(zidx: ZIDX, base: ZIDXBase) {
        this.zidx = zidx;
        this.base = base;
        this.root = zidx.peakidxShadowRoot;
    }

    // ─── Public API ─────────────────────────────────────────────────

    init() {
        const self = this;
        $(".zidxSearchAILink", this.root).on("click", function (e: JQuery.ClickEvent) {
            e.preventDefault();
            if (self.active) {
                self.deactivate();
                $(".zidxSearchMoreLink", self.root).show();
                $(".zidxSearchCancelAILink", self.root).addClass("zidxAISearchCancelled");
            } else {
                self.activate();
                $(".zidxSearchMoreLink", self.root).hide();
                $(".zidxSearchCancelAILink", self.root).removeClass("zidxAISearchCancelled");
            }
        });
        $(".zidxSearchCancelAILink", this.root).on("click", function (e: JQuery.ClickEvent) {
            e.preventDefault();
            self.deactivate();
            $(".zidxSearchMoreLink", self.root).show();
            $(".zidxSearchCancelAILink", self.root).addClass("zidxAISearchCancelled");
            self.zidx.zidxBase.checkForSearchFormChanges(true, true);
        });

        // Count result clicks for the listing search report. Only clicks made while the AI search is active.
        this.root.addEventListener("click", (e: Event) => {
            if (!self.active || !self.aiSessionId) return;
            const target = e.target as Element | null;
            const link = target ? target.closest("a.zidxSearchResultListingContainer") : null;
            const listingId = link ? link.getAttribute("data-id") : null;
            if (!listingId) return;
            const body = new URLSearchParams();
            body.append("ai_chat_session_id", self.aiSessionId);
            body.append("listing_id", listingId);
            fetch(self.zidx.options.selectedSiteDomain + "/ai-search-click/", {method: "POST", credentials: "include", body: body, keepalive: true})
                .catch((err) => console.error("Failed to record listing click", err));
        });

        this.checkForSessionRestore();
        let params = new URL(window.location.href).searchParams;
        let aiSearchText = params.get('aiSearchText');
        if (aiSearchText && aiSearchText != "") {
            this.aiSearchText = aiSearchText;
            $(".zidxSearchAILink", this.root).trigger("click");
        }
    }

    isActive(): boolean {
        return this.active;
    }

    // ─── URL / Session Management ───────────────────────────────────

    private checkForSessionRestore() {
        let self = this;
        setTimeout(function() {
            if (self.active) {
                return;
            }
            let params = new URL(window.location.href).searchParams;
            let sessionId = "";
            const match = window.location.pathname.match(/\/ai-search\/([a-zA-Z0-9_-]+)\/?$/);
            if (!match) return;
            sessionId = match[1];
            console.log("[AISearchIntegration] Restoring session from URL:", sessionId);

            self.originalPath = self.getBaseSearchPath();
            self.restoringSession = true;

            self.activate(sessionId);

            $(".zidxSearchMoreLink", self.root).hide();
            $(".zidxSearchCancelAILink", self.root).removeClass("zidxAISearchCancelled");
        }, 100);
    }

    private pushSessionURL(sessionId: string) {
        if (this.zidx.options.inAccount) {
            return;
        }

        const currentPath = window.location.pathname;

        // If the URL already contains this specific session ID, do absolutely nothing.
        // This preserves paths like /search-embed/31/ai-search/%sessionId%/ if loaded directly.
        if (currentPath.includes(`/ai-search/${sessionId}`)) {
            return;
        }

        // Otherwise, it's a brand new session generated on-the-fly; force the clean results path.
        // getPrefixURL keeps the site prefix (wordpress proxy, cdn) so a reload still reaches the proxy route
        const newPath = this.zidx.getPrefixURL("/results/ai-search/" + sessionId + "/");

        if (currentPath !== newPath) {
            let params = new URL(window.location.href).searchParams;
            let aiSearchText = params.get("aiSearchText");
            if (aiSearchText && aiSearchText != "") {
                this.aiSearchText = aiSearchText;
                params.delete("aiSearchText");
            }

            window.history.replaceState(
                { aiSearchSession: sessionId },
                "",
                newPath + (params.toString() ? "?" + params.toString() : "")
            );
        }
    }

    private restoreOriginalURL() {
        const restorePath = this.originalPath || this.getBaseSearchPath();
        if (window.location.pathname.includes("/ai-search/")) {
            window.history.replaceState({}, "", restorePath + window.location.search);
        }
        this.originalPath = null;
    }

    private getBaseSearchPath(): string {
        const path = window.location.pathname;
        const aiIdx = path.indexOf("/ai-search/");
        if (aiIdx !== -1) {
            return path.substring(0, aiIdx) + "/";
        }
        return path;
    }

    // ─── Activate / Deactivate ──────────────────────────────────────

    /**
     * @param restoreSessionId Existing session ID if recovering from URL
     * @param skipChatBoxInit Set to true when loaded from saved search to prevent early session initiation
     */
    private activate(restoreSessionId?: string, skipChatBoxInit = false) {
        this.active = true;
        this.lastPromptHadResults = false;

        if (!this.originalPath) {
            this.originalPath = this.getBaseSearchPath();
        }

        $(
            "#zidxSearchLocationContainer, " +
            "#zidxSearchPriceContainer, " +
            "#zidxSearchTypeContainer, " +
            "#zidxSearchBedroomsContainer, " +
            "#zidxSearchBathroomsContainer",
            this.root
        ).hide();

        $(".zidxSearchPopupDiv", this.root).hide();
        if (this.base.searchMorePopupOpen) {
            this.base.closeMorePopup();
        }

        this.$overlay = $(this.buildOverlayHTML());
        $(".zidxSearchBox", this.root).append(this.$overlay);

        $(".zidxSearchAILink", this.root).addClass("zidxActive");

        this.bindBarButtons();

        if (!skipChatBoxInit) {
            const chatContainer = $(".zidxAISearchChatEmbed", this.root)[0] as HTMLElement;
            this.createChatBox(chatContainer, restoreSessionId || null);
        }
    }

    private deactivate() {
        this.active = false;
        this.isSavedSearchMode = false;
        this.savedSearchParams = null;

        this.aiAdvancedSearchQueries.clear();
        this.aiVectorSearchQueries.clear();
        this.aiFieldWeights = {};
        this.barParser = null;
        this.restoringSession = false;
        this.lastSummaryText = "";

        if (this.aiChatBox?.eventSource) {
            this.aiChatBox.eventSource.close();
        }
        this.aiChatBox = null;

        if (this.$overlay) {
            this.$overlay.remove();
            this.$overlay = null;
        }

        $(
            "#zidxSearchLocationContainer, " +
            "#zidxSearchPriceContainer, " +
            "#zidxSearchTypeContainer, " +
            "#zidxSearchBedroomsContainer, " +
            "#zidxSearchBathroomsContainer, " +
            ".zidxSearchMoreSaveContainer",
            this.root
        ).show();

        $(".zidxSearchAILink", this.root).removeClass("zidxActive");
        $(".zidxAISearchOverlay", this.root).html("");

        this.restoreOriginalURL();
    }

    // ─── Overlay HTML ───────────────────────────────────────────────

    private buildOverlayHTML(): string {
        return (
            '<div class="zidxAISearchOverlay" style="width:100%; float:left; padding:0;">' +
            '<div class="zidxAISearchResultBar">' +
            '<div class="zidxAISearchResultBarContent" style="width:100%; float:left;"></div>' +
            '<div class="zidxAISearchResultBarActions" style="display:none; width:100%; float:left; margin-top:6px; text-align:right;">' +
            '<a href="#" class="zidxAISearchRefineBtn zidxDisableSPALink">Refine Search</a>' +
            '</div>' +
            '</div>' +
            '<div class="zidxAISearchChatEmbed" style="width:100%; float:left;"></div>' +
            '<div class="zidxAISearchMinimizeBar">' +
            '<a href="#" class="zidxAISearchMinimizeBtn zidxDisableSPALink" style="font-size:13px; font-weight:600; color:var(--link-color); text-decoration:none;">' +
            'Minimize Conversation</a>' +
            '</div>' +
            '</div>'
        );
    }

    private bindBarButtons() {
        const self = this;
        $(".zidxAISearchRefineBtn", this.root).off("click").on("click", (e: JQuery.ClickEvent) => {
            e.preventDefault();
            self.expandChat();
        });
        $(".zidxAISearchMinimizeBtn", this.root).off("click").on("click", (e: JQuery.ClickEvent) => {
            e.preventDefault();
            self.minimizeChat();
        });
    }

    // ─── Chat collapse / expand / minimize ──────────────────────────

    private collapseChatForPrompt() {
        const $chatEmbed = $(".zidxAISearchChatEmbed", this.root);
        const $bar = $(".zidxAISearchResultBar", this.root);
        const $barContent = $(".zidxAISearchResultBarContent", this.root);

        $barContent.empty();

        const loader = document.createElement("div");
        loader.className = "aifcChatLoadingDots";
        loader.innerHTML = '<span class="dot1"></span><span class="dot2"></span><span class="dot3"></span>';
        $barContent.append(loader);

        const barResponseDiv = document.createElement("div");
        barResponseDiv.className = "zidxAISearchBarResponse";
        barResponseDiv.style.display = "none";
        $barContent.append(barResponseDiv);

        this.barParser = new MarkdownStreamParser(barResponseDiv);

        $(".zidxAISearchResultBarActions", this.root).hide();
        $(".zidxAISearchMinimizeBar", this.root).hide();
        $bar.show();
        $chatEmbed.hide();
    }

    private minimizeChat() {
        if (this.lastSummaryText) {
            this.showStaticResultBar(this.lastSummaryText);
        } else {
            this.showStaticResultBar("AI search results");
        }
    }

    private showStaticResultBar(summaryText: string) {
        const $bar = $(".zidxAISearchResultBar", this.root);
        const $barContent = $(".zidxAISearchResultBarContent", this.root);

        $barContent.empty();

        const barDiv = document.createElement("div");
        barDiv.className = "zidxAISearchBarResponse";
        $barContent.append(barDiv);

        const p = new MarkdownStreamParser(barDiv);
        p.feed(summaryText);
        p.complete();

        this.barParser = null;

        let params = new URL(window.location.href).searchParams;
        // Keep standard UI visible during a un-refined saved search state
        if (!this.isSavedSearchMode) {
            $(".zidxAISearchResultBarActions", this.root).show();
            $(".zidxAISearchChatEmbed", this.root).hide();
            $(".zidxAISearchMinimizeBar", this.root).hide();
        } else {
            $(".zidxAISearchResultBarActions", this.root).show();
        }
        $bar.show();
    }

    /**
     * Expands the full chat history and shows minimize button
     * if we have search results to collapse back to.
     */
    private expandChat() {
        if (this.isSavedSearchMode) {
            this.isSavedSearchMode = false;
            const chatContainer = $(".zidxAISearchChatEmbed", this.root)[0] as HTMLElement;
            const responseTextElement = $(".zidxAISearchBarResponse", this.root)[0];

            // Extract the user-visible summary text from the response bar
            const textToRefine = responseTextElement ? responseTextElement.innerText.trim() : "";
            this.aiSearchText = "";

            // 1. Initialize the chat orchestrator container if it doesn't exist
            if (!this.aiChatBox) {
                this.createChatBox(chatContainer, null);
            }

            // 2. Only inject into history if we are actively converting from a saved search
            if (textToRefine && (window.location.href.indexOf("/search-embed/")!=-1 || window.location.href.indexOf("/account/saved-searches/edit")!=-1 || window.location.href.indexOf("/account/embeddable-searches/edit")!=-1)) {
                console.log("loading again?");
                setTimeout(() => {
                    const $responseBox = $(this.root).find(".aifcChatResponseBox");
                    if ($responseBox.length) {
                        // Match your typical client bubble HTML framework snippet
                        const clientMsgHtml = `
                            <div class="aifcChatBubbleRow aifcClientBubbleRow">
                                <div class="aifcChatBubble aifcClientBubble">
                                    ${textToRefine}
                                </div>
                            </div>
                        `;
                        $responseBox.append(clientMsgHtml);
                    }

                    // Pre-populate the text input for immediate tweaking without auto-submitting
                    $(".aifcChatPromptInput", this.root).focus();
                }, 150);
            }
        }

        $(".zidxAISearchResultBar", this.root).hide();

        const $chatEmbed = $(".zidxAISearchChatEmbed", this.root);
        $chatEmbed.show();

        $chatEmbed.find(".aifcChatResponseBoxContainer").show();

        if (this.lastPromptHadResults) {
            $(".zidxAISearchMinimizeBar", this.root).show();
        } else {
            $(".zidxAISearchMinimizeBar", this.root).hide();
        }

        const $input = $(".aifcChatPromptInput", this.root);
        if ($input.length && !$input.is(":focus")) {
            ($input[0] as HTMLInputElement).focus();
        }

        const $responseBox = $chatEmbed.find(".aifcChatResponseBox");
        if ($responseBox.length) {
            const lastChild = $responseBox[0].lastElementChild;
            if (lastChild) {
                setTimeout(() => {
                    lastChild.scrollIntoView({ behavior: 'smooth', block: 'end' });
                }, 75);
            }
        }
    }

    private removeAllLoadingDots() {
        $(".aifcChatLoadingDots", this.root).remove();
        $(this.root).find("#aifcCurrentLoader").remove();
    }

    // ─── AIChatBox wiring ───────────────────────────────────────────

    private createChatBox(container: HTMLElement, restoreSessionId: string | null) {
        const self = this;
        let tokenCountStarted = false;
        let barFirstTokenDone = false;

        const config = new AIChatBoxConfig();
        config.aiEndPointURL = "/ai-search/";
        config.aiBotId = 0;

        if (restoreSessionId) {
            config.initialSessionId = restoreSessionId;
            this.aiSessionId = restoreSessionId;
        }

        config.onSessionReady = (sessionId: string) => {
            self.aiSessionId = sessionId;
            self.pushSessionURL(sessionId);
        };

        config.onPromptCallback = (prompt: string): string => {
            tokenCountStarted = false;
            barFirstTokenDone = false;
            self.lastPromptHadResults = false;
            self.lastSummaryText = ""; // Clear this so the new stream/JSON stream can set it cleanly

            self.collapseChatForPrompt();

            return prompt;
        };
        let barStreamText = "";

        config.onText = (data:any) => {
            if (self.restoringSession) return data;
            if (!barFirstTokenDone) { barFirstTokenDone = true; self.onBarFirstToken(); }
            if (self.barParser) self.barParser.feed(data);
            barStreamText += data;
            if (barStreamText.trim()) self.lastSummaryText = barStreamText.trim();
            return data;
        };

        config.onJSON = (data: any) => {
            console.log("[AISearchIntegration] onJSON — AI search params received:", data);
            if (!data) return;

            let sp: SearchParams;
            try {
                sp = SearchParams.fromJSON(data);
            } catch (e) {
                console.error("[AISearchIntegration] Failed to parse SearchParams:", e);
                return;
            }

            self.lastPromptHadResults = true;

            // Keep querySummary internal to the JSON state data tracking
            if (data.querySummary) {
                self.querySummary = data.querySummary;
            }

            // ONLY touch lastSummaryText here if it's completely empty (like during an initial session restore/history load)
            // If onText or onDataCompleteCallback already filled it, leave it alone!
            if (!self.lastSummaryText) {
                const fallbackSummary = data.summary || data.searchSummary || data.querySummary || "AI Search Results";
                self.lastSummaryText = fallbackSummary;
            }

            self.aiAdvancedSearchQueries.clear();
            self.aiVectorSearchQueries.clear();
            for (const idx of sp.listingIndices) {
                if (idx.advancedSearchQuery && idx.advancedSearchQuery !== "") {
                    self.aiAdvancedSearchQueries.set(idx.listingIndexId, idx.advancedSearchQuery);
                }
            }
            if (sp.vectorSearch && sp.vectorSearch !== "") {
                self.aiVectorSearchQueries.set(0, sp.vectorSearch);
            }
            if (sp.fieldWeights && Object.keys(sp.fieldWeights).length > 0) {
                self.aiFieldWeights = sp.fieldWeights;
            } else {
                self.aiFieldWeights = {};
            }

            self.resetFormQuietly();
            if (sp.sort) {
                const sortMap: Record<string, string> = {
                    "newest":     "0",
                    "price_desc": "2",
                    "price_asc":  "1",
                    "relevance":  "3",
                };
                const sortVal = sortMap[sp.sort] || "0";
                $("#zidxSearchSortField", self.root).val(sortVal);
            }

            self.base.applySearchParamsToForm(sp, () => {
                $("#zidx_offset", self.root).val(0);
                self.base.resetListingData = true;
                self.base.checkForSearchFormChanges(true, true);
            });
        };

        config.onHistoryLoaded = (messages: any[]) => {
            self.removeAllLoadingDots();
            let summary = "AI Search Results";
            let assistantText = "";
            let hasSearch = false;

            for (let i = messages.length - 1; i >= 0; i--) {
                let message = messages[i];

                // capture the initial NON-json assistant text (what originally streamed via onText)
                if (!assistantText && message.role === "assistant" && message.content) {
                    assistantText = message.content;
                }

                if (message.toolOutput != "") {
                    let toolOutputParsed = JSON.parse(message.toolOutput);
                    if (toolOutputParsed.querySummary != "") {
                        // prefer the streamed intro text; querySummary is only a fallback
                        summary = assistantText || toolOutputParsed.querySummary;
                        self.lastSummaryText = summary;
                        self.lastPromptHadResults = true;
                        hasSearch = true;

                        self.showStaticResultBar(summary);
                        setTimeout(() => {
                            config.onJSON(toolOutputParsed);
                        }, 0);
                        break;
                    }
                }
            }
            if (!hasSearch) {
                $(".zidxAISearchChatEmbed", this.root).show();
            }
            self.restoringSession = false;
        };

        config.onTokens = (count: number) => {
            if (!tokenCountStarted) {
                self.aiChatBox?.resetProgressBar();
                tokenCountStarted = true;
            }
            self.aiChatBox?.updateProgressBar(count);
        };

        config.onDataCompleteCallback = () => {
            if (self.restoringSession) return;

            if (self.barParser) {
                self.barParser.complete();
            }

            // Capture the exact, final text that was just rendered to the user
            const $responseDiv = $(".zidxAISearchBarResponse", self.root);
            if ($responseDiv.length) {
                self.lastSummaryText = $responseDiv.text().trim();
            }

            if (self.lastPromptHadResults || barFirstTokenDone) {
                $(".zidxAISearchResultBarActions", self.root).show();
            } else {
                self.expandChat();
            }
        };

        this.aiChatBox = new AIChatBox(
            this.zidx,
            config,
            this.root,
            container,
            function() {
                if (self.aiSearchText != "") {
                    console.log("new aichatbox with aisearchtext", self.aiSearchText);
                    setTimeout(function() {
                        $(".aifcChatPromptInput", self.root).val(self.aiSearchText);
                        $(".aifcChatPromptSubmit", self.root).trigger("click");
                        self.aiSearchText = "";
                    }, 100);
                }
            }
        );
    }

    private onBarFirstToken() {
        $(".zidxAISearchResultBarContent .aifcChatLoadingDots", this.root).remove();
        $(".zidxAISearchResultBarContent #aifcCurrentLoader", this.root).remove();
        $(".zidxAISearchBarResponse", this.root).show();
    }

    /**
     * Activates AI search mode programmatically from a saved search.
     * Modified to cache parameters and prevent immediate instantiation of AIChatBox.
     */
    activateFromSavedSearch(sp: SearchParams) {
        this.isSavedSearchMode = true;
        this.savedSearchParams = sp;

        if (!this.active) {
            // Pass true to skip initialization of AIChatBox until refinement is requested
            this.activate(undefined, true);
            $(".zidxSearchMoreLink", this.root).hide();
            $(".zidxSearchCancelAILink", this.root).removeClass("zidxAISearchCancelled");
            if (sp.querySummary != "") {
                let summary = sp.querySummary;
                this.aiSearchText = summary;
            }
        }

        this.aiAdvancedSearchQueries.clear();
        this.aiVectorSearchQueries.clear();
        for (const idx of sp.listingIndices) {
            if (idx.advancedSearchQuery && idx.advancedSearchQuery !== "") {
                this.aiAdvancedSearchQueries.set(idx.listingIndexId, idx.advancedSearchQuery);
            }
        }
        if (sp.vectorSearch && sp.vectorSearch !== "") {
            this.aiVectorSearchQueries.set(0, sp.vectorSearch);
        }
        if (sp.fieldWeights && Object.keys(sp.fieldWeights).length > 0) {
            this.aiFieldWeights = sp.fieldWeights;
        }

        const summary = (sp as any).summary || sp.querySummary || "AI Search Results";
        console.log("updated summary ", summary);
        if (summary && this.lastSummaryText=="") {
            this.lastSummaryText = summary;
            this.lastPromptHadResults = true;
            this.showStaticResultBar(summary);
        }
    }

    // ─── Form reset ─────────────────────────────────────────────────

    private resetFormQuietly() {
        const root = this.root;
        $(".zidxSearchBox select", root).each(function (this: HTMLElement) {
            (this as HTMLSelectElement).selectedIndex = 0;
        });
        $(".zidxSearchBox input[type='checkbox']", root).each(function (this: HTMLElement) {
            (this as HTMLInputElement).checked = false;
        });
        $(".zidxSearchMorePopupFieldContainer input[type='text']", root).val("");
        $("#zidx_search_polygon, #zidx_search_latitude, #zidx_search_longitude", root).val("");
        $("#zidx_search_sort", root).val("1");
        $("#zidx_offset", root).val("0");

        if (this.base.locationMegaMenu) {
            this.base.locationMegaMenu.clear();
        }
    }
}